/* ============================================================
   GOLDEN STAR JEWELLERY — IN-STORE PICKUP LINE
   File: assets/gsj-pickup.js
   Pairs with snippets/gsj-pickup-availability.liquid.

   Responsibilities
   - Expand/collapse the store panel (delegated, survives swaps)
   - Feature the right store: saved choice → silent IP geolocation
     (cached 30 days, 1.5s timeout, no permission popup) → server
     default. Only ever swaps between AVAILABLE stores.
   - "PICK UP HERE" remembers the shopper's store
   - Today/tomorrow wording from the same-day cutoff (Pacific)
   - Store-transfer promise pills: next-day before the transfer
     cutoff, 2-day after
   - Variant changes: re-fetch the widget via the Section
     Rendering API (?section_id=gsj-pickup-availability) and swap
     the mount's inner HTML

   House rules: no requestAnimationFrame loops. The only timer is
   a 1.2s variant-id poll, gated on document visibility, matching
   the approved passive-listener pattern.
   ============================================================ */

(function () {
  'use strict';

  if (window.__gsjPickupInit) return;
  window.__gsjPickupInit = true;

  var LS_STORE = 'gsj_pickup_store';
  var LS_GEO = 'gsj_pickup_geo';
  var GEO_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var GEO_ENDPOINT = 'https://get.geojs.io/v1/ip/geo.json';
  var SECTION_ID = 'gsj-pickup-availability';

  function mount() { return document.getElementById('gsj-pickup-mount'); }
  function root() { return document.getElementById('gsj-pickup'); }

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  /* ---------- today / tomorrow window ---------- */

  function pacificHour() {
    try {
      return parseInt(new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Vancouver', hour: 'numeric', hourCycle: 'h23'
      }).format(new Date()), 10);
    } catch (e) {
      return new Date().getHours(); /* shopper-local fallback */
    }
  }

  function applyWindow(m, r) {
    var h = pacificHour();
    var cut = parseInt(m.getAttribute('data-cutoff'), 10);
    var later = !isNaN(cut) && h >= cut;
    r.setAttribute('data-window', later ? 'later' : 'today');
    var prefix = r.querySelector('[data-gsj-pu-prefix]');
    var sub = r.querySelector('[data-gsj-pu-sub]');
    if (prefix) prefix.textContent = m.getAttribute(later ? 'data-line-later' : 'data-line-today');
    if (sub) sub.textContent = m.getAttribute(later ? 'data-sub-later' : 'data-sub-today');
    var pills = r.querySelectorAll('[data-gsj-pu-pill][data-state="stock"]');
    for (var i = 0; i < pills.length; i++) {
      pills[i].textContent = m.getAttribute(later ? 'data-pill-later' : 'data-pill-today');
    }
    /* Store-transfer promise pills (no local stock, other store has it):
       next-day wording before the transfer cutoff, 2-day wording after. */
    var tcut = parseInt(m.getAttribute('data-transfer-cutoff'), 10);
    var tLater = !isNaN(tcut) && h >= tcut;
    var tpills = r.querySelectorAll('[data-gsj-pu-pill][data-state="transfer"]');
    for (var j = 0; j < tpills.length; j++) {
      tpills[j].textContent = m.getAttribute(tLater ? 'data-pill-transfer-2day' : 'data-pill-transfer-next');
    }
  }

  /* ---------- featured store ---------- */

  function cards(r) {
    return Array.prototype.slice.call(r.querySelectorAll('[data-gsj-store]'));
  }

  function availableCards(r) {
    return cards(r).filter(function (c) { return c.getAttribute('data-available') === 'true'; });
  }

  function setFeatured(r, locationId) {
    var target = r.querySelector('[data-gsj-store][data-location-id="' + locationId + '"]');
    if (!target || target.getAttribute('data-available') !== 'true') return;
    r.setAttribute('data-featured-id', locationId);
    var nameEl = r.querySelector('[data-gsj-pu-storename]');
    if (nameEl) nameEl.textContent = target.getAttribute('data-short-name');
    cards(r).forEach(function (c) {
      c.classList.toggle('is-featured', c === target);
    });
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var rad = Math.PI / 180;
    var dLat = (lat2 - lat1) * rad;
    var dLng = (lng2 - lng1) * rad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function nearestAvailableId(r, lat, lng) {
    var best = null;
    var bestKm = Infinity;
    availableCards(r).forEach(function (c) {
      var cLat = parseFloat(c.getAttribute('data-lat'));
      var cLng = parseFloat(c.getAttribute('data-lng'));
      if (isNaN(cLat) || isNaN(cLng)) return;
      var km = haversineKm(lat, lng, cLat, cLng);
      if (km < bestKm) { bestKm = km; best = c.getAttribute('data-location-id'); }
    });
    return best;
  }

  function cachedGeo() {
    var raw = lsGet(LS_GEO);
    if (!raw) return null;
    try {
      var g = JSON.parse(raw);
      if (g && typeof g.la === 'number' && typeof g.lo === 'number' &&
          (Date.now() - (g.t || 0)) < GEO_TTL_MS) return g;
    } catch (e) { /* ignore corrupt cache */ }
    return null;
  }

  function applyFeatured(r) {
    var pref = lsGet(LS_STORE);
    if (pref) { setFeatured(r, pref); return true; }
    var geo = cachedGeo();
    if (geo) {
      var id = nearestAvailableId(r, geo.la, geo.lo);
      if (id) { setFeatured(r, id); return true; }
    }
    return false;
  }

  function scheduleGeoLookup() {
    if (lsGet(LS_STORE) || cachedGeo()) return;
    var run = function () {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 1500) : null;
      fetch(GEO_ENDPOINT, controller ? { signal: controller.signal } : undefined)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (timer) clearTimeout(timer);
          var la = parseFloat(data && data.latitude);
          var lo = parseFloat(data && data.longitude);
          if (isNaN(la) || isNaN(lo)) return;
          lsSet(LS_GEO, JSON.stringify({ la: la, lo: lo, t: Date.now() }));
          var r = root();
          if (r) applyFeatured(r);
        })
        .catch(function () { if (timer) clearTimeout(timer); /* keep default store */ });
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 2500);
    }
  }

  /* ---------- panel + choose (delegated) ---------- */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-gsj-pu-toggle]') : null;
    if (toggle) {
      var r = root();
      if (!r) return;
      var panel = r.querySelector('.gsj-pickup__panel');
      if (!panel) return;
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
      return;
    }
    var choose = event.target.closest ? event.target.closest('[data-gsj-pu-choose]') : null;
    if (choose) {
      var r2 = root();
      if (!r2) return;
      var id = choose.getAttribute('data-location-id');
      lsSet(LS_STORE, id);
      setFeatured(r2, id);
      var t = r2.querySelector('[data-gsj-pu-toggle]');
      var p = r2.querySelector('.gsj-pickup__panel');
      if (t && p) { t.setAttribute('aria-expanded', 'false'); p.hidden = true; }
    }
  });

  /* ---------- variant changes ---------- */

  function currentVariantId() {
    try {
      var fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return fromUrl;
    } catch (e) { /* very old browsers */ }
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  var refetching = false;

  function refetchForVariant(variantId) {
    var m = mount();
    if (!m || refetching) return;
    refetching = true;
    var url = m.getAttribute('data-section-url') +
      '?variant=' + encodeURIComponent(variantId) +
      '&section_id=' + SECTION_ID;
    fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        refetching = false;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('gsj-pickup-mount');
        var m2 = mount();
        if (!m2) return;
        m2.setAttribute('data-variant-id', variantId);
        m2.innerHTML = fresh ? fresh.innerHTML : '';
        var r = root();
        if (r) {
          applyWindow(m2, r);
          applyFeatured(r);
        }
      })
      .catch(function () { refetching = false; });
  }

  function checkVariant() {
    var m = mount();
    if (!m) return;
    var vid = currentVariantId();
    if (vid && vid !== m.getAttribute('data-variant-id')) refetchForVariant(vid);
  }

  document.addEventListener('change', function (event) {
    var t = event.target;
    if (t && t.name === 'id' && t.form && /\/cart\/add/.test(t.form.getAttribute('action') || '')) {
      setTimeout(checkVariant, 50);
    }
  });

  setInterval(function () {
    if (document.visibilityState === 'visible') checkVariant();
  }, 1200);

  /* ---------- boot ---------- */

  function boot() {
    var m = mount();
    var r = root();
    if (m && r) {
      applyWindow(m, r);
      applyFeatured(r);
    }
    scheduleGeoLookup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
